// src/utils/imageCompression.js

const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

export async function compressImage(file, opts = {}) {
  const {
    maxBytes = 2 * 1024 * 1024,
    maxWidth = 2000,
    maxHeight = 2000,
    preferredMime = "image/webp",
  } = opts;

  // Pass SVG through (lossless)
  if (file.type === "image/svg+xml") {
    const dataURL = await readAsDataURL(file);
    return { blob: file, dataURL, mime: "image/svg+xml" };
  }

  const srcUrl = await readAsDataURL(file);
  const img = await loadImage(srcUrl);

  const width = Math.max(1, img.width || 1);
  const height = Math.max(1, img.height || 1);
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);

  const canWebP = (() => {
    try { return canvas.toDataURL("image/webp").startsWith("data:image/webp"); }
    catch { return false; }
  })();
  const mime = canWebP ? preferredMime : "image/jpeg";

  let qMin = 0.5;
  let qMax = 0.95;
  let best = { blob: null, dataURL: "", size: Infinity };

  // Binary-search quality
  for (let i = 0; i < 7; i++) {
    const q = (qMin + qMax) / 2;
    const dataURL = canvas.toDataURL(mime, q);
    const res = await fetch(dataURL);
    const blob = await res.blob();
    const size = blob.size;
    if (size <= maxBytes) {
      best = { blob, dataURL, size };
      qMin = q;
    } else {
      qMax = q;
    }
  }

  if (!best.blob) {
    const dataURL = canvas.toDataURL(mime, qMin);
    const res = await fetch(dataURL);
    const blob = await res.blob();
    return { blob, dataURL, mime };
  }

  return { blob: best.blob, dataURL: best.dataURL, mime };
}
